/**
 * OnboardingWizard
 *
 * Full-page, blocking 5-step wizard shown on first login:
 *   Step 0 — Welcome: confirm name, detect timezone/language, bootstrap agent.md
 *   Step 1 — Create Your Agent: name, personality, system prompt
 *   Step 2 — Voice & Integrations: VAPI voice picker + integration toggles
 *   Step 3 — Train Your Agent: file / URL upload for knowledge base
 *   Step 4 — Test Chat: mini chat panel to verify the agent works
 *
 * "Skip" is available on every step. Completing or skipping sets
 * onboardingComplete = true in Firebase RTDB and navigates to /dashboard.
 */

import React, { useState, useRef, useContext } from 'react';
import {
  CCard,
  CCardBody,
  CCardFooter,
  CButton,
  CFormInput,
  CFormTextarea,
  CFormSelect,
  CFormSwitch,
  CFormLabel,
  CSpinner,
  CAlert,
  CProgress,
  CContainer,
  CRow,
  CCol,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import {
  cilArrowLeft,
  cilArrowRight,
  cilCheckCircle,
  cilSend,
  cilCloudUpload,
  cilFile,
  cilLink,
  cilUser,
  cilSettings,
  cilChatBubble,
  cilLibrary,
  cilSpeedometer,
} from '@coreui/icons';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { AuthUserContext } from '../../session';
import { useOnboarding } from '../../../context/OnboardingContext';
import config from '../../../utils/EnvironmentSwitcher';
import { enqueueAgentChatTask, waitForAgentTask } from '../../../services/AgentTaskService';

// ---- Constants ----

const STEPS = [
  'Welcome',
  'Create Your Agent',
  'Voice & Integrations',
  'Train Your Agent',
  'Test Chat',
];

const VOICE_OPTIONS = [
  { label: 'Default (no voice)', value: '' },
  { label: 'Alloy', value: 'alloy' },
  { label: 'Echo', value: 'echo' },
  { label: 'Fable', value: 'fable' },
  { label: 'Onyx', value: 'onyx' },
  { label: 'Nova', value: 'nova' },
  { label: 'Shimmer', value: 'shimmer' },
];

// Icons for each step (displayed in the step indicator)
const STEP_ICONS = [cilUser, cilSettings, cilSpeedometer, cilLibrary, cilChatBubble];

const OnboardingWizard = () => {
  const authUser = useContext(AuthUserContext);
  const navigate = useNavigate();
  const { setOnboardingComplete, setFeatureTourDismissed, refreshAgentCount } = useOnboarding();

  // ---- Step state ----
  const [step, setStep] = useState(0);
  const [error, setError] = useState(null);

  // ---- Step 0: Welcome ----
  const [displayName, setDisplayName] = useState(authUser?.displayName || authUser?.username || '');
  const [timezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
  const [language] = useState(navigator.language || 'en');

  // ---- Step 1: Agent basics ----
  const [agentName, setAgentName] = useState('');
  const [personality, setPersonality] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');

  // ---- Step 2: Voice & Integrations ----
  const [voiceId, setVoiceId] = useState('');
  const [integrations, setIntegrations] = useState({
    calendar: false,
    phone: false,
    crm: false,
  });

  // ---- Step 3: Knowledge upload ----
  const [knowledgeFiles, setKnowledgeFiles] = useState([]); // array of File objects
  const [urlInput, setUrlInput] = useState('');
  const [urls, setUrls] = useState([]); // array of url strings queued
  const [uploading, setUploading] = useState(false);

  // ---- Step 4: Test Chat ----
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);

  // ---- Created agent id (set after step 2 completes) ----
  const [createdAgentId, setCreatedAgentId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // ---- Helpers ----

  /** Get a Firebase ID token */
  const getToken = async () => {
    const auth = getAuth();
    return auth.currentUser?.getIdToken();
  };

  /** Toggle an integration switch */
  const toggleIntegration = (key) => {
    setIntegrations((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  /** Can the user advance from the current step? */
  const canAdvance = () => {
    if (step === 0) return displayName.trim().length > 0;
    if (step === 1) return agentName.trim().length > 0;
    return true;
  };

  // ---- Core actions ----

  /**
   * Bootstrap the agent.md profile with initial info.
   * Called when advancing past step 0 (Welcome).
   */
  const bootstrapProfile = async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${config.apiUrl}/api/profile/agent-md`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          markdown: [
            `# Agent Profile: ${displayName}`,
            '',
            '## Identity',
            `- **Name**: ${displayName}`,
            `- **Preferred Name**: ${displayName.split(' ')[0]}`,
            `- **Timezone**: ${timezone}`,
            `- **Language**: ${language}`,
            '',
            '## Communication Style',
            '- (To be learned from conversations)',
            '',
            '## Health Patterns',
            '- (To be learned from health data interactions)',
            '',
            '## Interests',
            '- (To be learned from conversations)',
            '',
            '## Goals',
            '- (To be learned from conversations)',
            '',
            '## Interaction History',
            `- ${new Date().toISOString().split('T')[0]}: Account created, onboarding started`,
            '',
            '## Learned Preferences',
            '- (To be learned over time)',
          ].join('\n'),
        }),
      });
      if (!res.ok) {
        // 404 = backend not deployed with profile routes — non-blocking
        console.warn('OnboardingWizard: profile bootstrap returned', res.status);
      }
    } catch (err) {
      // Non-blocking — profile can be created later
      console.warn('OnboardingWizard: bootstrap profile failed', err);
    }
  };

  /**
   * Create the agent (called when advancing past step 2).
   * Stores the created agent ID for subsequent steps.
   */
  const createAgent = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const token = await getToken();
      const body = {
        name: agentName.trim(),
        personality: personality.trim(),
        systemPrompt: systemPrompt.trim(),
        voiceId: voiceId || null,
        integrations,
      };
      const res = await fetch(`${config.apiUrl}/api/agents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const text = await res.text();
        if (res.status === 404) {
          setError(
            'Agent API not available (404). The backend may need to be redeployed with the latest code. Click "Skip for now" to continue to the dashboard.',
          );
        } else {
          setError(text || `Create failed: ${res.status}`);
        }
        return null;
      }
      const data = await res.json();
      const id = data.agent?.id || data.id;
      setCreatedAgentId(id);
      await refreshAgentCount();
      return id;
    } catch (err) {
      console.error('OnboardingWizard: create agent error', err);
      setError(
        err.message?.includes('404') || err.message?.includes('Router not found')
          ? 'Agent API not available. Click "Skip for now" to continue to the dashboard.'
          : err.message,
      );
      return null;
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Upload queued knowledge files and URLs (called when advancing past step 3).
   */
  const uploadKnowledge = async (agentId) => {
    if (!agentId) return;
    setUploading(true);
    setError(null);
    const token = await getToken();

    // Upload files
    for (const file of knowledgeFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        await fetch(`${config.apiUrl}/api/agents/${agentId}/knowledge`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
      } catch (err) {
        console.warn(`OnboardingWizard: failed to upload file ${file.name}`, err);
      }
    }

    // Upload URLs
    for (const url of urls) {
      try {
        await fetch(`${config.apiUrl}/api/agents/${agentId}/knowledge`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ url }),
        });
      } catch (err) {
        console.warn(`OnboardingWizard: failed to upload URL ${url}`, err);
      }
    }

    setUploading(false);
  };

  /**
   * Handle the "Next" button. Certain steps trigger side-effects.
   */
  const handleNext = async () => {
    // Step 0 -> 1: bootstrap agent.md
    if (step === 0) {
      await bootstrapProfile();
    }

    // Step 2 -> 3: create the agent
    if (step === 2) {
      const id = await createAgent();
      if (!id) return; // error state set inside createAgent
    }

    // Step 3 -> 4: upload knowledge
    if (step === 3 && createdAgentId) {
      await uploadKnowledge(createdAgentId);
    }

    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  /**
   * Finish onboarding (called on the last step or via "Skip").
   */
  const handleFinish = async () => {
    await setOnboardingComplete(true);
    navigate('/dashboard');
  };

  /**
   * Skip the entire wizard.
   */
  const handleSkip = async () => {
    await setOnboardingComplete(true);
    // User chose to skip onboarding — don't show the feature tour when they land on dashboard
    await setFeatureTourDismissed(true);
    navigate('/dashboard');
  };

  // ---- Test Chat handler (OpenClaw queue; worker uses chat.mieros.cloud/gptj) ----
  const handleSendChat = async () => {
    const text = chatInput.trim();
    if (!text || !createdAgentId) return;

    const userMsg = { role: 'user', content: text };
    setChatMessages((prev) => [...prev, userMsg]);
    setChatInput('');
    setChatLoading(true);

    try {
      const token = await getToken();
      const queued = await enqueueAgentChatTask({
        token,
        agentId: createdAgentId,
        messages: [...chatMessages, userMsg],
      });
      const taskId = queued.task_id || queued.taskId;

      if (!taskId) {
        throw new Error('Task queued but no task ID returned');
      }

      const task = await waitForAgentTask({ token, taskId });

      if (task.status === 'failed') {
        setChatMessages((prev) => [
          ...prev,
          { role: 'assistant', content: task.error || 'Task failed.' },
        ]);
        return;
      }

      const data = task.result || {};
      const content =
        data.reply ||
        data.output ||
        data.summary ||
        data.choices?.[0]?.message?.content ||
        data.error ||
        (typeof data === 'string' ? data : null) ||
        'No response.';
      setChatMessages((prev) => [...prev, { role: 'assistant', content }]);
    } catch (err) {
      setChatMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `Error: ${err.message}` },
      ]);
    } finally {
      setChatLoading(false);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  };

  // ---- Step renderers ----

  /** Step 0: Welcome */
  const renderWelcome = () => (
    <div className="text-center">
      {/* Animated logo / icon area */}
      <div
        style={{
          width: 100,
          height: 100,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, var(--electric-blue), var(--electric-purple))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px',
          boxShadow: '0 0 40px rgba(0, 212, 255, 0.3)',
        }}
      >
        <CIcon icon={cilUser} size="3xl" style={{ color: '#fff' }} />
      </div>
      <h2 style={{ color: 'var(--text-primary)', fontFamily: "'Space Grotesk', sans-serif" }}>
        Welcome to MiExistence
      </h2>
      <p style={{ color: 'var(--text-secondary)', maxWidth: 480, margin: '12px auto 32px' }}>
        Your personal AI-powered platform for health tracking, digital assets, and intelligent
        agents. Let's set up your first AI agent in a few quick steps.
      </p>

      <div style={{ maxWidth: 400, margin: '0 auto', textAlign: 'left' }}>
        <CFormLabel style={{ color: 'var(--text-secondary)' }}>Your Name</CFormLabel>
        <CFormInput
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="How should we address you?"
          className="mb-3"
        />
        <CRow className="g-3">
          <CCol sm={6}>
            <CFormLabel style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
              Timezone (auto-detected)
            </CFormLabel>
            <CFormInput value={timezone} disabled style={{ opacity: 0.7 }} />
          </CCol>
          <CCol sm={6}>
            <CFormLabel style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
              Language
            </CFormLabel>
            <CFormInput value={language} disabled style={{ opacity: 0.7 }} />
          </CCol>
        </CRow>
      </div>

      {/* Platform capabilities overview */}
      <div className="d-flex justify-content-center gap-4 mt-4 flex-wrap">
        {[
          { icon: cilSpeedometer, label: 'Health Dashboard' },
          { icon: cilSettings, label: 'AI Agents' },
          { icon: cilLibrary, label: 'Knowledge Base' },
          { icon: cilChatBubble, label: 'Smart Chat' },
        ].map((item) => (
          <div
            key={item.label}
            style={{
              background: 'var(--glass-bg)',
              border: '1px solid var(--glass-border)',
              borderRadius: 12,
              padding: '16px 20px',
              minWidth: 120,
              textAlign: 'center',
            }}
          >
            <CIcon icon={item.icon} size="lg" style={{ color: 'var(--electric-blue)' }} />
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', marginTop: 6 }}>
              {item.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  /** Step 1: Create Your Agent */
  const renderAgentBasics = () => (
    <>
      <h4 style={{ color: 'var(--text-primary)' }} className="mb-3">
        Name and configure your AI agent
      </h4>
      <div className="mb-3">
        <CFormLabel style={{ color: 'var(--text-secondary)' }}>Agent Name *</CFormLabel>
        <CFormInput
          value={agentName}
          onChange={(e) => setAgentName(e.target.value)}
          placeholder="e.g. Customer Support Bot, Personal Health Assistant"
        />
      </div>
      <div className="mb-3">
        <CFormLabel style={{ color: 'var(--text-secondary)' }}>Personality / Description</CFormLabel>
        <CFormTextarea
          rows={3}
          value={personality}
          onChange={(e) => setPersonality(e.target.value)}
          placeholder="A friendly, professional assistant that handles customer queries..."
        />
      </div>
      <div className="mb-3">
        <CFormLabel style={{ color: 'var(--text-secondary)' }}>System Prompt</CFormLabel>
        <CFormTextarea
          rows={5}
          value={systemPrompt}
          onChange={(e) => setSystemPrompt(e.target.value)}
          placeholder="You are a helpful AI assistant for..."
        />
        <small style={{ color: 'var(--text-muted)' }}>
          This is the base instruction sent to the LLM for every conversation.
        </small>
      </div>
    </>
  );

  /** Step 2: Voice & Integrations */
  const renderVoiceIntegrations = () => (
    <>
      <h4 style={{ color: 'var(--text-primary)' }} className="mb-3">
        Configure voice and integrations
      </h4>
      <div className="mb-4">
        <CFormLabel style={{ color: 'var(--text-secondary)' }}>Voice (VAPI)</CFormLabel>
        <CFormSelect value={voiceId} onChange={(e) => setVoiceId(e.target.value)}>
          {VOICE_OPTIONS.map((v) => (
            <option key={v.value} value={v.value}>
              {v.label}
            </option>
          ))}
        </CFormSelect>
        <small style={{ color: 'var(--text-muted)' }}>
          Select a voice for phone/voice interactions. Leave empty for text-only.
        </small>
      </div>

      <h5 style={{ color: 'var(--text-primary)' }} className="mb-3">
        Integrations
      </h5>
      {Object.entries(integrations).map(([key, enabled]) => (
        <div key={key} className="d-flex justify-content-between align-items-center mb-3">
          <span style={{ color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{key}</span>
          <CFormSwitch
            checked={enabled}
            onChange={() => toggleIntegration(key)}
            label={enabled ? 'On' : 'Off'}
          />
        </div>
      ))}
      <small style={{ color: 'var(--text-muted)' }}>
        Integration connections are configured after setup. Toggle to indicate interest.
      </small>
    </>
  );

  /** Step 3: Train Your Agent */
  const renderKnowledgeUpload = () => (
    <>
      <h4 style={{ color: 'var(--text-primary)' }} className="mb-2">
        Train your agent with knowledge
      </h4>
      <p style={{ color: 'var(--text-secondary)' }} className="mb-4">
        Upload documents or paste URLs. Your agent will use these as context when answering
        questions. You can skip this and add knowledge later.
      </p>

      {/* File upload */}
      <div className="mb-4">
        <CFormLabel style={{ color: 'var(--text-secondary)' }}>
          <CIcon icon={cilFile} className="me-1" /> Upload Files (PDF, TXT, DOCX, MD)
        </CFormLabel>
        <CFormInput
          type="file"
          accept=".pdf,.txt,.docx,.doc,.md"
          multiple
          onChange={(e) => {
            const files = Array.from(e.target.files || []);
            setKnowledgeFiles((prev) => [...prev, ...files]);
          }}
        />
        {knowledgeFiles.length > 0 && (
          <div className="mt-2">
            {knowledgeFiles.map((f, i) => (
              <span
                key={i}
                className="badge me-1 mb-1"
                style={{
                  background: 'rgba(0, 212, 255, 0.15)',
                  color: 'var(--electric-blue)',
                  border: '1px solid rgba(0, 212, 255, 0.3)',
                  padding: '6px 10px',
                  borderRadius: 8,
                }}
              >
                {f.name}
                <span
                  style={{ cursor: 'pointer', marginLeft: 6, opacity: 0.7 }}
                  onClick={() => setKnowledgeFiles((prev) => prev.filter((_, idx) => idx !== i))}
                >
                  x
                </span>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* URL input */}
      <div className="mb-3">
        <CFormLabel style={{ color: 'var(--text-secondary)' }}>
          <CIcon icon={cilLink} className="me-1" /> Or paste URLs
        </CFormLabel>
        <div className="d-flex gap-2">
          <CFormInput
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="https://example.com/docs"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && urlInput.trim()) {
                setUrls((prev) => [...prev, urlInput.trim()]);
                setUrlInput('');
              }
            }}
          />
          <CButton
            color="primary"
            disabled={!urlInput.trim()}
            onClick={() => {
              if (urlInput.trim()) {
                setUrls((prev) => [...prev, urlInput.trim()]);
                setUrlInput('');
              }
            }}
          >
            <CIcon icon={cilCloudUpload} />
          </CButton>
        </div>
        {urls.length > 0 && (
          <div className="mt-2">
            {urls.map((u, i) => (
              <span
                key={i}
                className="badge me-1 mb-1"
                style={{
                  background: 'rgba(124, 58, 237, 0.15)',
                  color: 'var(--accent-light)',
                  border: '1px solid rgba(124, 58, 237, 0.3)',
                  padding: '6px 10px',
                  borderRadius: 8,
                }}
              >
                {u}
                <span
                  style={{ cursor: 'pointer', marginLeft: 6, opacity: 0.7 }}
                  onClick={() => setUrls((prev) => prev.filter((_, idx) => idx !== i))}
                >
                  x
                </span>
              </span>
            ))}
          </div>
        )}
      </div>

      {uploading && (
        <CProgress value={50} animated className="mt-3" style={{ height: 6 }} />
      )}
    </>
  );

  /** Step 4: Test Chat */
  const renderTestChat = () => (
    <>
      <h4 style={{ color: 'var(--text-primary)' }} className="mb-2">
        Test your agent
      </h4>
      <p style={{ color: 'var(--text-secondary)' }} className="mb-3">
        Send a message to see how your agent responds. This uses the system prompt and any
        knowledge you uploaded.
      </p>

      {/* Chat area */}
      <div
        style={{
          minHeight: 250,
          maxHeight: 350,
          overflowY: 'auto',
          background: 'rgba(0,0,0,0.15)',
          borderRadius: 12,
          padding: 16,
          marginBottom: 12,
        }}
      >
        {chatMessages.length === 0 && (
          <p className="text-center" style={{ color: 'var(--text-muted)', paddingTop: 50 }}>
            Send a message to test your agent.
          </p>
        )}
        {chatMessages.map((msg, i) => (
          <div
            key={i}
            className={`d-flex mb-2 ${msg.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}
          >
            <div
              style={{
                maxWidth: '75%',
                padding: '10px 14px',
                borderRadius: 12,
                background:
                  msg.role === 'user'
                    ? 'linear-gradient(135deg, var(--electric-blue), var(--electric-purple))'
                    : 'rgba(255,255,255,0.06)',
                color: msg.role === 'user' ? '#fff' : 'var(--text-primary)',
                fontSize: '0.9rem',
                whiteSpace: 'pre-wrap',
              }}
            >
              {msg.content}
            </div>
          </div>
        ))}
        {chatLoading && (
          <div className="d-flex justify-content-start mb-2">
            <div
              style={{
                padding: '10px 14px',
                borderRadius: 12,
                background: 'rgba(255,255,255,0.06)',
              }}
            >
              <CSpinner size="sm" />
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Input */}
      <div className="d-flex gap-2">
        <CFormInput
          value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
          placeholder="Type a message..."
          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendChat()}
        />
        <CButton color="primary" onClick={handleSendChat} disabled={chatLoading || !chatInput.trim()}>
          <CIcon icon={cilSend} />
        </CButton>
      </div>

      {!createdAgentId && (
        <CAlert color="warning" className="mt-3">
          No agent was created yet (you may have skipped the creation step). You can still finish
          onboarding and create an agent later.
        </CAlert>
      )}
    </>
  );

  /** Render the current step content */
  const renderStep = () => {
    switch (step) {
      case 0:
        return renderWelcome();
      case 1:
        return renderAgentBasics();
      case 2:
        return renderVoiceIntegrations();
      case 3:
        return renderKnowledgeUpload();
      case 4:
        return renderTestChat();
      default:
        return null;
    }
  };

  // ---- Main render ----
  // Wrapper is scrollable so mobile users can reach Skip/Next (overflow-y: auto, touch-friendly)
  return (
    <div
      className="onboarding-wizard mieros-gradient-bg min-vh-100 d-flex justify-content-center"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        alignItems: 'flex-start',
      }}
    >
      <CContainer
        className="onboarding-wizard-inner"
        style={{ maxWidth: 680, paddingTop: 24, paddingBottom: 32 }}
      >
        {/* Step indicator — wraps on small screens via .onboarding-wizard SCSS */}
        <div className="onboarding-wizard-steps d-flex justify-content-center gap-2 mb-4">
          {STEPS.map((label, i) => (
            <div
              key={label}
              className="text-center"
              style={{
                opacity: i <= step ? 1 : 0.4,
                transition: 'opacity 0.3s ease',
                minWidth: 70,
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  background:
                    i < step
                      ? 'linear-gradient(135deg, var(--electric-green), var(--electric-blue))'
                      : i === step
                        ? 'linear-gradient(135deg, var(--electric-blue), var(--electric-purple))'
                        : 'var(--glass-bg)',
                  border: `1px solid ${i <= step ? 'rgba(0, 212, 255, 0.4)' : 'var(--glass-border)'}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 6px',
                  transition: 'all 0.3s ease',
                }}
              >
                {i < step ? (
                  <CIcon icon={cilCheckCircle} style={{ color: '#fff' }} />
                ) : (
                  <CIcon icon={STEP_ICONS[i]} style={{ color: i === step ? '#fff' : 'var(--text-muted)' }} />
                )}
              </div>
              <small style={{ color: i === step ? 'var(--electric-blue)' : 'var(--text-muted)', fontSize: '0.7rem' }}>
                {label}
              </small>
            </div>
          ))}
        </div>

        {/* Progress bar */}
        <CProgress
          value={((step + 1) / STEPS.length) * 100}
          className="mb-4"
          style={{ height: 4, borderRadius: 2 }}
        />

        {/* Error display */}
        {error && (
          <CAlert color="danger" className="mb-3">
            {error}
          </CAlert>
        )}

        {/* Card with step content */}
        <CCard
          style={{
            background: 'var(--glass-bg)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid var(--glass-border)',
            borderRadius: 16,
            overflow: 'hidden',
          }}
        >
          <CCardBody className="p-4">{renderStep()}</CCardBody>

          <CCardFooter
            className="d-flex justify-content-between align-items-center"
            style={{
              background: 'rgba(0, 0, 0, 0.1)',
              borderTop: '1px solid var(--glass-border)',
            }}
          >
            {/* Left side: Back or Skip */}
            <div className="d-flex gap-2">
              {step > 0 && (
                <CButton
                  color="secondary"
                  variant="outline"
                  onClick={() => setStep((s) => s - 1)}
                  disabled={submitting || uploading}
                >
                  <CIcon icon={cilArrowLeft} className="me-1" /> Back
                </CButton>
              )}
              <CButton
                color="link"
                onClick={handleSkip}
                style={{ color: 'var(--text-muted)', textDecoration: 'none' }}
                disabled={submitting || uploading}
              >
                Skip for now
              </CButton>
            </div>

            {/* Right side: Next or Finish */}
            {step < STEPS.length - 1 ? (
              <CButton
                color="primary"
                disabled={!canAdvance() || submitting || uploading}
                onClick={handleNext}
              >
                {submitting || uploading ? (
                  <CSpinner size="sm" className="me-2" />
                ) : null}
                Next <CIcon icon={cilArrowRight} className="ms-1" />
              </CButton>
            ) : (
              <CButton color="primary" onClick={handleFinish}>
                <CIcon icon={cilCheckCircle} className="me-2" />
                Get Started
              </CButton>
            )}
          </CCardFooter>
        </CCard>
      </CContainer>
    </div>
  );
};

export default OnboardingWizard;


